import { useRef } from 'react';

import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import { triggerTracking } from '@zoopla/tracking';
import {
    RegisterUserV3Document,
    RegisterUserV3Mutation,
    RegisterUserV3MutationVariables
} from '../queries/registerUser.generated';
import {
    logNetworkAuthFailure,
    logUserAuthFailure,
    trackAuthFailure,
    trackAuthSuccess,
    trackNextBestActionOptin
} from './authMutationHook.tracking';
import { AuthMutationHook } from './authMutationHook.types';
import { useAuth } from './useAuth';
import { trackAuthSegmentEvent } from '../utils/segmentTrack';
import { getNextBestAction } from '../utils/getNextBestAction';

const registrationErrorCodes: { [key: string]: string } = {
    EMAIL_EXISTS: 'This email address is already in use',
    EMAIL_DELETED: 'This email address is already in use',
    BAD_EMAIL_FORMAT: 'The email address you entered is invalid',
    WEAK_PASSWORD:
        'Password must be at least 8 characters containing 1 uppercase letter, 1 lowercase letter, 1 number and 1 special character'
};

export type RegisterInput = {
    email: string;
    password: string;
    consent__insights__24: boolean;
};

type ErrorFieldValues = 'email' | 'password';

export class FieldError extends Error {
    field: ErrorFieldValues;

    constructor(message: string, field: ErrorFieldValues) {
        super(message);
        this.field = field;
    }
}

export const isFieldError = (error: Error | FieldError): error is FieldError =>
    error instanceof FieldError;

export const useRegister: AuthMutationHook<RegisterInput> = ({
    trackingData
}) => {
    const hasNetworkError = useRef(false);
    const { setCurrentSession } = useAuth();
    const { lazyFetch: registerMutation } = useLazyFetchGraphql<
        RegisterUserV3Mutation,
        RegisterUserV3MutationVariables
    >(RegisterUserV3Document, {
        onError(error) {
            if (error && 'isNetworkError' in error) {
                hasNetworkError.current = true;
            }
        }
    });

    const trackAndThrowError = (
        message: string,
        field?: ErrorFieldValues,
        requestId?: string
    ) => {
        trackAuthFailure({
            source: 'zpg',
            type: 'registration',
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });

        if (field) {
            throw new FieldError(message, field);
        }

        const requestIdMessage = requestId ? ` (Request ID: ${requestId})` : '';

        throw new Error(`${message}${requestIdMessage}`);
    };

    const authenticate = async (registerInput: RegisterInput) => {
        const { email, password, consent__insights__24 } = registerInput;

        const input = {
            email,
            password,
            consents: [
                {
                    consentName: 'consent.insights.24',
                    consentGiven: consent__insights__24
                }
            ]
        };

        const response = await registerMutation({ input });

        if (hasNetworkError.current) {
            logNetworkAuthFailure({ type: 'register' });
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again. (Network error)'
            );
        }

        if (!response.data || response.errors) {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                undefined,
                response.extensions?.requestId
            );
        }

        const {
            data: { registerUserV3 }
        } = response;

        if (registerUserV3?.__typename === 'AuthError') {
            if (!(registerUserV3.errorCode in registrationErrorCodes)) {
                return trackAndThrowError(
                    'Sorry, something went wrong. Please try again.',
                    undefined,
                    response.extensions?.requestId
                );
            }

            const message = registrationErrorCodes[registerUserV3.errorCode];

            if (registerUserV3.errorCode === 'WEAK_PASSWORD') {
                return trackAndThrowError(message, 'password');
            }

            if (registerUserV3.errorCode === 'EMAIL_EXISTS') {
                triggerTracking({
                    event: 'GAEvent',
                    eventCategory: 'registration',
                    eventAction: 'failure_account_exists',
                    eventLabel: trackingData.label
                });
            }

            return trackAndThrowError(message, 'email');
        }

        if (!registerUserV3?.user) {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                undefined,
                response.extensions?.requestId
            );
        }

        // This covers the case we have seen in a few incidents with sessions
        // when the authentication API works well but the user one fails
        if (!registerUserV3.user.loggedIn || !registerUserV3.user.userUuid) {
            logUserAuthFailure({
                type: 'register',
                subtype: !registerUserV3.user.userUuid
                    ? 'no_uuid'
                    : 'not_logged_in'
            });
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again later.',
                undefined,
                response.extensions?.requestId
            );
        }

        const { user } = registerUserV3;

        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'permission opt-in',
            eventAction: registerInput.consent__insights__24
                ? 'optin_both'
                : 'optin_neither',
            eventLabel: trackingData.label
        });

        trackNextBestActionOptin(
            consent__insights__24,
            trackingData?.segmentData?.page
        );

        if (consent__insights__24) {
            trackAuthSegmentEvent('optin', trackingData.segmentData, user);
        }

        trackAuthSuccess({
            source: 'zpg',
            type: 'registration',
            userObject: user,
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });

        setCurrentSession(user, true);

        const nextBestActions = getNextBestAction(user, true);

        return { ...user, actions: nextBestActions };
    };

    return authenticate;
};
